/**
 * M03 Playbook 编辑器 · S2 Day 7b
 *
 * 数据源：
 * - usePlaybookYaml(pb_id) → GET /admin/v1/playbook/{pb_id}/yaml
 * - usePutPlaybookYaml(pb_id) → PUT 同路径
 * - useSkillsCatalog → GET /admin/v1/playbook/skills_catalog（多选组件参考清单）
 * - useValidatePrefetch → POST /admin/v1/playbook/validate-prefetch（pre_fetch_spec dry-run）
 *
 * 设计：
 * - 主体：Monaco YAML 编辑器（与 M02 L2 编辑同范式 · schema 校验后端 SSOT · 乐观锁）
 * - 左侧辅助：
 *   - PB 选择（list 数据驱动 · 切 PB 自动载入新 YAML）
 *   - SK 多选参考面板（仅展示 SK01-SK11 + name · 不修改 YAML · 让运营对照写 allowed_skills）
 *   - pre_fetch_spec 独立 dry-run 校验框（粘贴片段 + 点校验 → 显示 valid/parsed/errors）
 *
 * 设计取舍：YAML 整体编辑 + 子段独立校验 = MVP 简单可靠（schema 单源后端）
 *   不做"字段表单 → 反序列化为 YAML"的双向绑定（S3+ 真表单化）
 */
import Editor, { type OnChange } from "@monaco-editor/react";
import { useEffect, useMemo, useState } from "react";

import { SnapshotPanel } from "@/components/shared/snapshot-panel";
import { Select } from "@/components/ui/select";
import {
	usePlaybookList,
	usePlaybookYaml,
	usePutPlaybookYaml,
	useSkillsCatalog,
	useValidatePrefetch,
} from "@/lib/api/playbook";
import { skillLabel } from "@/lib/product-labels";

interface ApiErrorBody {
	detail?: string;
}

function extractErrorDetail(err: unknown): string {
	if (typeof err === "object" && err !== null) {
		const maybeAxios = err as { response?: { data?: ApiErrorBody }; message?: string };
		if (maybeAxios.response?.data?.detail) return maybeAxios.response.data.detail;
		if (maybeAxios.message) return maybeAxios.message;
	}
	return String(err);
}

interface Props {
	selectedPbId: string | null;
	onSelectPbId: (pbId: string) => void;
}

interface BranchPreview {
	key: string;
	displayName: string;
	subTasks: string[];
	allowedSkills: string[];
}

function parseInlineList(value: string): string[] {
	const normalized = value.trim().replace(/^\[/, "").replace(/\]$/, "");
	if (!normalized) return [];
	return normalized
		.split(",")
		.map((item) => item.trim().replace(/^"|"$/g, ""))
		.filter(Boolean);
}

function extractYamlScalar(block: string, key: string): string {
	const match = new RegExp(`^\\s+${key}:\\s*(.+)$`, "m").exec(block);
	return (match?.[1] ?? "").replace(/^"|"$/g, "").trim();
}

function parseBranchPreview(yaml: string): BranchPreview[] {
	const lines = yaml.split("\n");
	const start = lines.findIndex((line) => /^branches:\s*$/.test(line));
	if (start < 0) return [];
	const branchLines: string[] = [];
	for (const line of lines.slice(start + 1)) {
		if (/^\S/.test(line)) break;
		branchLines.push(line);
	}
	const normalized = branchLines.join("\n").replace(/^\s{2}([A-Za-z0-9_]+):\s*$/m, "$1:");
	const chunks = normalized
		.replace(/\n\s{2}([A-Za-z0-9_]+):\s*$/gm, "\n---BRANCH---\n$1:")
		.split("\n---BRANCH---\n")
		.filter((chunk) => /^[A-Za-z0-9_]+:/m.test(chunk));
	return chunks.map((chunk) => {
		const key = (/^([A-Za-z0-9_]+):/m.exec(chunk)?.[1] ?? "").trim();
		return {
			key,
			displayName: extractYamlScalar(chunk, "display_name") || key,
			subTasks: parseInlineList(extractYamlScalar(chunk, "sub_tasks")),
			allowedSkills: parseInlineList(extractYamlScalar(chunk, "allowed_skills")),
		};
	});
}

const SAMPLE_PREFETCH_YAML = `auto_invoke_if_extractable:
  - skill_id: SK01
    trigger_slots: [order_id]
    trigger_mode: any
rag_namespaces: [policy]
top_k: 3
`;

export function PlaybookEditor({ selectedPbId, onSelectPbId }: Props) {
	const listQuery = usePlaybookList();
	const yamlQuery = usePlaybookYaml(selectedPbId);
	const skillsQuery = useSkillsCatalog();
	const putMutation = usePutPlaybookYaml(selectedPbId ?? "");
	const validateMutation = useValidatePrefetch();

	const [draft, setDraft] = useState<string>("");
	const [loadedVersion, setLoadedVersion] = useState<string | null>(null);
	const [savedAt, setSavedAt] = useState<string | null>(null);
	const [conflictMsg, setConflictMsg] = useState<string | null>(null);

	const [prefetchYaml, setPrefetchYaml] = useState<string>(SAMPLE_PREFETCH_YAML);

	useEffect(() => {
		if (yamlQuery.data) {
			setDraft(yamlQuery.data.yaml_text);
			setLoadedVersion(yamlQuery.data.parsed_version);
			setSavedAt(null);
			setConflictMsg(null);
		}
	}, [yamlQuery.data]);

	const isDirty = yamlQuery.data ? draft !== yamlQuery.data.yaml_text : false;
	const branchPreview = useMemo(() => parseBranchPreview(draft), [draft]);

	const handleSave = () => {
		if (!selectedPbId) return;
		setConflictMsg(null);
		setSavedAt(null);
		putMutation.mutate(
			{ yaml_text: draft, expected_version: loadedVersion },
			{
				onSuccess: (res) => {
					setSavedAt(new Date().toLocaleTimeString());
					setLoadedVersion(res.parsed_version);
				},
				onError: (err) => {
					const detail = extractErrorDetail(err);
					const status = (err as { response?: { status?: number } })?.response?.status;
					if (status === 409) {
						setConflictMsg(`乐观锁冲突 · 后端版本已变 · 请重新载入。原始错误：${detail}`);
					} else {
						setConflictMsg(`保存失败：${detail}`);
					}
				},
			},
		);
	};

	const handleValidatePrefetch = () => {
		validateMutation.mutate({ pre_fetch_spec_yaml: prefetchYaml });
	};

	const handleEditorChange: OnChange = (value) => {
		setDraft(value ?? "");
	};

	const pbOptions = listQuery.data?.items ?? [];
	const skills = skillsQuery.data?.items ?? [];

	return (
		<div className="space-y-3" data-testid="pb-editor">
			{selectedPbId ? (
				<SnapshotPanel
					module={{ kind: "playbook", pb_id: selectedPbId }}
					moduleLabel={`${selectedPbId} 方案`}
					testidPrefix={`pb-snapshot-${selectedPbId}`}
				/>
			) : null}
			<div className="flex flex-wrap items-end gap-3">
				<div className="flex flex-col text-sm">
					<span className="mb-1 text-text-secondary">选择处理方案</span>
					<Select
						className="w-full"
						aria-label="选择处理方案"
						data-testid="pb-editor-select"
						value={selectedPbId ?? ""}
						onChange={(e) => onSelectPbId(e.target.value)}
					>
						<option value="">— 选择 —</option>
						{pbOptions.map((pb) => (
							<option key={pb.playbook_id} value={pb.playbook_id}>
								{pb.name}（{pb.playbook_id}）
							</option>
						))}
					</Select>
				</div>

				{loadedVersion ? (
					<div className="text-sm text-text-secondary">
						当前版本 <span className="font-mono text-xs">{loadedVersion}</span>
						{isDirty ? (
							<span className="ml-2 rounded border border-brand-warning/30 bg-brand-warning/10 px-2 py-0.5 text-xs text-brand-warning">
								草稿未保存
							</span>
						) : null}
						{savedAt ? (
							<span
								data-testid="pb-editor-saved-at"
								className="ml-2 rounded border border-brand-success/30 bg-brand-success/10 px-2 py-0.5 text-xs text-brand-success"
							>
								已保存 {savedAt}
							</span>
						) : null}
					</div>
				) : null}

				<div className="ml-auto flex gap-2">
					<button
						type="button"
						className="rounded-md border border-brand-accent bg-brand-accent px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
						data-testid="pb-editor-save"
						onClick={handleSave}
						disabled={!selectedPbId || putMutation.isPending || !isDirty}
					>
						{putMutation.isPending ? "保存中..." : "保存"}
					</button>
				</div>
			</div>

			{!selectedPbId ? (
				<div className="rounded-md border border-dashed border-surface-border p-6 text-center text-sm text-text-muted">
					请从上方选择处理方案，或在“方案列表”里点击一行。
				</div>
			) : yamlQuery.isLoading ? (
				<div data-testid="pb-editor-loading" className="p-4 text-sm text-text-secondary">
					加载方案配置中...
				</div>
			) : yamlQuery.isError ? (
				<div data-testid="pb-editor-error" className="p-4 text-sm text-brand-danger">
					加载失败：{extractErrorDetail(yamlQuery.error)}
				</div>
			) : (
				<>
					<div className="rounded-md border border-surface-border" data-testid="pb-editor-monaco">
						<Editor
							height="50vh"
							language="yaml"
							value={draft}
							onChange={handleEditorChange}
							options={{
								minimap: { enabled: false },
								fontSize: 13,
								tabSize: 2,
								wordWrap: "on",
								scrollBeyondLastLine: false,
							}}
						/>
					</div>

					<section
						className="rounded-md border border-surface-border"
						data-testid="pb-editor-branch-preview"
					>
						<div className="border-b border-surface-border bg-surface-subtle px-3 py-2 text-xs text-text-secondary">
							方案分支预览
						</div>
						{branchPreview.length > 0 ? (
							<div className="overflow-auto p-2">
								<table className="w-full border-collapse text-xs">
									<thead>
										<tr className="text-left">
											<th className="px-2 py-1 font-medium">分支</th>
											<th className="px-2 py-1 font-medium">子任务</th>
											<th className="px-2 py-1 font-medium">白名单</th>
										</tr>
									</thead>
									<tbody>
										{branchPreview.map((branch) => (
											<tr key={branch.key} className="border-t border-surface-border">
												<td className="px-2 py-1">
													<div className="font-medium text-text-primary">{branch.displayName}</div>
													<div className="font-mono text-[11px] text-text-muted">{branch.key}</div>
												</td>
												<td className="px-2 py-1 text-text-secondary">
													{branch.subTasks.join(" / ") || "-"}
												</td>
												<td className="px-2 py-1 font-mono text-[11px] text-text-primary">
													{branch.allowedSkills.join(", ") || "沿用 PB 默认"}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						) : (
							<div className="px-3 py-2 text-xs text-text-muted">PB 默认路径</div>
						)}
					</section>

					{conflictMsg ? (
						<div
							data-testid="pb-editor-conflict"
							className="rounded-md border border-brand-danger bg-brand-danger/10 p-3 text-sm text-brand-danger"
						>
							{conflictMsg}
						</div>
					) : null}

					<div className="grid gap-3 lg:grid-cols-2">
						<section className="space-y-2 rounded-md border border-surface-border p-3">
							<h3 className="text-sm font-medium">工具参考清单</h3>
							<p className="text-xs text-text-muted">
								运营对照工具名称和编号填写允许工具；这里不直接编辑方案正文。
							</p>
							{skillsQuery.isLoading ? (
								<p className="text-xs text-text-secondary">加载工具目录中...</p>
							) : (
								<ul className="space-y-1 text-xs" data-testid="pb-editor-sk-list">
									{skills.map((sk) => (
										<li
											key={sk.skill_id}
											data-testid={`pb-editor-sk-${sk.skill_id}`}
											className="flex items-baseline gap-2"
										>
											<span>{skillLabel(sk.skill_id)}</span>
											<span className="ml-auto text-text-muted">{sk.category}</span>
										</li>
									))}
								</ul>
							)}
						</section>

						<section
							className="space-y-2 rounded-md border border-surface-border p-3"
							data-testid="pb-editor-prefetch"
						>
							<h3 className="text-sm font-medium">预取配置校验</h3>
							<p className="text-xs text-text-muted">
								粘贴预取配置片段后点击校验，确认生成回复前会准备哪些事实。
							</p>
							<textarea
								className="h-32 w-full rounded-md border border-surface-border bg-surface-card p-2 font-mono text-xs"
								value={prefetchYaml}
								onChange={(e) => setPrefetchYaml(e.target.value)}
								data-testid="pb-editor-prefetch-input"
							/>
							<div className="flex gap-2">
								<button
									type="button"
									className="rounded-md border border-surface-border px-3 py-1.5 text-xs hover:bg-surface-subtle"
									data-testid="pb-editor-prefetch-validate"
									onClick={handleValidatePrefetch}
									disabled={validateMutation.isPending}
								>
									{validateMutation.isPending ? "校验中..." : "校验预取配置"}
								</button>
							</div>
							{validateMutation.data ? (
								validateMutation.data.valid ? (
									<div
										data-testid="pb-editor-prefetch-valid"
										className="rounded border border-brand-success/30 bg-brand-success/10 p-2 text-xs text-brand-success"
									>
										✅ 合法 · top_k={validateMutation.data.parsed?.top_k} · namespaces=
										{(validateMutation.data.parsed?.rag_namespaces ?? []).join("/") || "—"}
									</div>
								) : (
									<div
										data-testid="pb-editor-prefetch-invalid"
										className="rounded border border-brand-danger bg-brand-danger/10 p-2 text-xs text-brand-danger"
									>
										❌ 失败：
										<ul className="mt-1 list-inside list-disc">
											{validateMutation.data.errors.map((e) => (
												<li key={e}>{e}</li>
											))}
										</ul>
									</div>
								)
							) : null}
						</section>
					</div>
				</>
			)}

			<p className="text-xs text-text-muted">
				配置校验以后端规则为准；页面只负责编辑、保存和展示错误。
			</p>
		</div>
	);
}
